<script setup lang="ts">
// ================================
// Une section repliable du parcours, à en-tête porteur (DEV-2284, lot A).
//
// - Rang 1 (temps 2, 3, 4) : deux lignes. Ligne 1 : numéro, titre court,
//   noyau, pastille ; ligne 2 : le complément, en gris.
// - Rang 2 (détails, groupes du cadre) : une ligne : libellé, noyau,
//   complément, pastille. Sous 640 px le complément se tait, sauf s'il porte
//   une réserve, auquel cas il passe à la ligne.
// - Trois formes : repliée (un bouton à chevron, des valeurs), sans objet
//   (une ligne sans chevron qui dit pourquoi), absente (rien).
// - Dépliée, la section reprend son titre complet et sa question, et son
//   en-tête se tait. Un bouton d'en-tête (`#action`) est un VOISIN du bouton
//   de repli, jamais son enfant.
// - Sans objet, ce que la section garde de lisible (ses sections de détail,
//   l'historique) s'écrit sous la ligne : aucun repli ne cache un fait.
// - Le contenu replié reste dans le document (v-show) : la recherche du
//   navigateur y passe, et rien de ce qui est en cours n'est démonté.
// ================================
import { computed } from "vue"
import type { Entete } from "@modules/courtage/utils/optim-turpe-v2.entetes"
import OptimTurpeV2TexteSigne from "./OptimTurpeV2TexteSigne.vue"

type Props = {
    /** Identifiant d'ancre (`m-t2`, `acc-contrat`…). */
    id: string
    entete: Entete
    rang: 1 | 2
    ouvert: boolean
    /** Le numéro du temps (rang 1). */
    numero?: number | null
    /** Le titre complet, repris quand la section est dépliée. */
    titre?: string | null
    /** La question du lecteur, sous le titre déplié (rang 1). */
    question?: string | null
}
const props = withDefaults(defineProps<Props>(), {
    numero: null,
    titre: null,
    question: null,
})

const emit = defineEmits<{ "update:ouvert": [ouvert: boolean] }>()

const getCorpsId = computed(() => `${props.id}-corps`)
const getRepliable = computed(() => props.entete.forme === "repliee")
const getTitre = computed(() => props.titre ?? props.entete.libelle)

function basculer(): void {
    emit("update:ouvert", !props.ouvert)
}
</script>

<template>
    <section
        v-if="entete.forme !== 'absente'"
        :id="id"
        class="optim-turpe-v2-section scroll-mt-36"
        :class="
            rang === 1
                ? 'rounded-lg border border-surface-200 bg-white'
                : 'border-t border-surface-100'
        "
        :data-forme="entete.forme"
        :data-ouvert="getRepliable && ouvert ? '1' : '0'"
        :data-testid="`optim-turpe-v2-section-${id}`"
    >
        <div
            class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2"
            :class="rang === 1 ? 'px-5 py-3' : 'py-2'"
        >
            <!-- Repliée : le bouton de repli porte l'en-tête entier ; le
                 lecteur d'écran lit le libellé d'abord, les valeurs ensuite. -->
            <button
                v-if="getRepliable"
                type="button"
                class="flex min-w-0 flex-1 items-start gap-2 text-left"
                :class="$slots.action ? 'basis-full sm:basis-0' : ''"
                :aria-expanded="ouvert"
                :aria-controls="getCorpsId"
                data-testid="optim-turpe-v2-section-bascule"
                @click="basculer"
            >
                <!-- Le chevron se centre sur la PREMIÈRE ligne du titre : sa
                     boîte a la hauteur de ligne du titre (24 px au rang 1,
                     20 px au rang 2) et l'icône n'hérite plus de la hauteur de
                     ligne du bouton (retour de relecture du 07/10). -->
                <span
                    class="flex shrink-0 items-center"
                    :class="rang === 1 ? 'h-6' : 'h-5'"
                    data-testid="optim-turpe-v2-section-chevron"
                >
                    <BaseIcon
                        :icon="
                            ouvert
                                ? 'pi pi-chevron-down'
                                : 'pi pi-chevron-right'
                        "
                        size="0.75rem"
                        class="flex leading-none text-primary-400"
                    />
                </span>
                <span
                    v-if="ouvert"
                    class="flex min-w-0 flex-col"
                    data-testid="optim-turpe-v2-section-titre"
                >
                    <span
                        :class="
                            rang === 1
                                ? 'text-base font-semibold text-primary-900'
                                : 'text-sm font-bold text-primary-900'
                        "
                    >
                        <span v-if="numero" class="mr-1.5 text-primary-500">{{
                            numero
                        }}</span
                        >{{ getTitre }}
                    </span>
                    <span
                        v-if="question && rang === 1"
                        class="text-sm text-primary-500"
                        >{{ question }}</span
                    >
                </span>
                <span
                    v-else
                    class="flex min-w-0 flex-col"
                    data-testid="optim-turpe-v2-section-entete"
                >
                    <span
                        class="flex flex-wrap items-baseline gap-x-1.5"
                        :class="rang === 1 ? 'text-base' : 'text-sm'"
                    >
                        <span v-if="numero" class="text-primary-500">{{
                            numero
                        }}</span>
                        <span class="font-bold text-primary-900">{{
                            entete.libelle
                        }}</span>
                        <span
                            v-if="entete.noyau"
                            class="font-medium text-primary-800"
                            >· <OptimTurpeV2TexteSigne :texte="entete.noyau"
                        /></span>
                        <span
                            v-if="entete.complement && rang === 2"
                            class="font-medium text-primary-600"
                            :class="
                                entete.pastilleReserve
                                    ? 'basis-full sm:basis-auto'
                                    : 'hidden sm:inline'
                            "
                            >·
                            <OptimTurpeV2TexteSigne :texte="entete.complement"
                        /></span>
                        <BaseTag
                            v-if="entete.pastille"
                            :value="entete.pastille"
                            :severity="
                                entete.pastilleReserve ? 'warn' : 'secondary'
                            "
                            class="text-xs"
                        />
                    </span>
                    <span
                        v-if="entete.complement && rang === 1"
                        class="text-sm text-primary-500"
                        ><OptimTurpeV2TexteSigne :texte="entete.complement"
                    /></span>
                </span>
            </button>

            <!-- Sans objet : une ligne sans chevron, qui dit pourquoi. -->
            <p
                v-else
                class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 text-primary-600"
                :class="rang === 1 ? 'text-base' : 'text-sm'"
                data-testid="optim-turpe-v2-section-sans-objet"
            >
                <span v-if="numero" class="text-primary-400">{{ numero }}</span>
                <span class="font-bold">{{ entete.libelle }}</span>
                <span v-if="entete.noyau">· {{ entete.noyau }}</span>
                <span v-if="entete.complement" class="text-primary-500"
                    >· {{ entete.complement }}</span
                >
            </p>

            <!-- Le bouton d'en-tête, voisin du bouton de repli. -->
            <div
                v-if="$slots.action"
                class="flex flex-wrap items-center gap-2"
                data-testid="optim-turpe-v2-section-action"
            >
                <slot name="action" />
            </div>
        </div>

        <div
            v-if="getRepliable"
            v-show="ouvert"
            :id="getCorpsId"
            role="region"
            :aria-label="getTitre"
            :class="rang === 1 ? 'flex flex-col gap-4 px-5 pb-5' : 'pb-3'"
        >
            <slot />
        </div>
        <!-- Sans objet : ce que la section garde de lisible (ses détails, une
             étude passée) reste sous la ligne, sans repli. -->
        <div
            v-else-if="$slots.default"
            :class="rang === 1 ? 'flex flex-col gap-4 px-5 pb-5' : 'pb-3'"
            data-testid="optim-turpe-v2-section-sous-la-ligne"
        >
            <slot />
        </div>
    </section>
</template>
